import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/shared/PasswordInput';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, UserPlus } from 'lucide-react';

export function AdminContinuityCard() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', initial_password: '' });
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/client/admins').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const appoint = async () => {
    setBusy(true);
    try {
      await api.post('/client/admins', form);
      toast.success(L('Co-administrador designado. Deberá cambiar su contraseña al ingresar.',
        'Co-admin appointed. They must change their password on first login.'));
      setForm({ name: '', email: '', initial_password: '' });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const setActive = async (uid, active) => {
    try {
      await api.patch(`/client/admins/${uid}`, { active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;
  return (
    <Card className="p-5 shadow-card space-y-3" data-testid="admin-continuity-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">{L('Administradores de la organización', 'Organization administrators')}</h3>
        {data.continuity_risk && (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs" variant="outline" data-testid="continuity-warning">
            {L('Riesgo de continuidad: un solo administrador activo', 'Continuity risk: only one active administrator')}
          </Badge>
        )}
      </div>
      <ul className="space-y-1.5" data-testid="admins-list">
        {data.admins.map((a) => (
          <li key={a.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
            <span className="flex-1 truncate">{a.name} <span className="text-xs text-muted-foreground">({a.email})</span></span>
            <Badge variant="outline" className={`text-[10px] ${a.status === 'inactive' ? 'bg-slate-100' : 'bg-emerald-100 text-emerald-800'}`}>
              {a.status || 'active'}
            </Badge>
            <Button size="sm" variant="ghost" className="h-7 text-xs"
                    onClick={() => setActive(a.id, a.status === 'inactive')}
                    data-testid={`admin-toggle-${a.id}`}>
              {a.status === 'inactive' ? L('Reactivar', 'Reactivate') : L('Desactivar', 'Deactivate')}
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-end gap-2 pt-1 border-t border-border">
        <div><Label className="text-xs">{L('Nombre', 'Name')}</Label>
          <Input className="h-8 w-44" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coadmin-name" /></div>
        <div><Label className="text-xs">Email</Label>
          <Input className="h-8 w-52" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coadmin-email" /></div>
        <div><Label className="text-xs">{L('Contraseña inicial', 'Initial password')}</Label>
          <PasswordInput className="h-8 w-52" value={form.initial_password}
                 onChange={(e) => setForm({ ...form, initial_password: e.target.value })} autoComplete="new-password" data-testid="coadmin-password" /></div>
        <Button size="sm" onClick={appoint} disabled={busy} data-testid="coadmin-add">
          <UserPlus className="h-4 w-4 mr-1" /> {L('Designar co-administrador', 'Appoint co-admin')}
        </Button>
      </div>
    </Card>
  );
}

export default AdminContinuityCard;
